import { Diagnostics } from './internal/Diagnostics.mjs';
import { Parser } from './internal/Parser.mjs';
import { Serializer } from './internal/Serializer.mjs';
import { FieldDefinitionsUtilities } from './FieldDefinitions.utilities.mjs';

class FqlToMqlUtilities {
  static #_POSTURES = new Set(['strict', 'interactive']);
  static #_OBJECT_ID_RE = /^[0-9a-fA-F]{24}$/;
  static #_DATE_RE = /^\d{4}-\d{2}-\d{2}([T ].+)?$/;
  static #_REGEX_SPECIALS_RE = /[.*+?^${}()|[\]\\]/g;
  static #_RANGE_OPERATOR_KEYS = new Set(['$gt', '$gte', '$lt', '$lte']);
  static #_EJSON_SCALAR_KEYS = new Set(['$oid', '$date']);

  static convert(query, fieldDefinitions, options = {}) {
    FieldDefinitionsUtilities.assertValid(fieldDefinitions);
    let posture = 'strict';
    if (options.posture !== undefined) {
      if (!FqlToMqlUtilities.#_POSTURES.has(options.posture)) {
        throw new Error('INVALID_POSTURE');
      }
      posture = options.posture;
    }
    const ast = FqlToMqlUtilities.#_resolveAst(query, fieldDefinitions);
    const context = {
      fields: fieldDefinitions.fields,
      interactive: posture === 'interactive',
      diagnostics: ast.diagnostics.map((diagnostic) => ({ ...diagnostic, span: { ...diagnostic.span } }))
    };
    let fragment = null;
    if (ast.child !== null) {
      fragment = FqlToMqlUtilities.#_convertNode(ast.child, context);
      const warnsPerChild = ast.child.type === 'and' || ast.child.type === 'or';
      if (fragment === null && context.interactive && !warnsPerChild) {
        FqlToMqlUtilities.#_warnSkipped(ast.child, context);
      }
    }
    const filter = FqlToMqlUtilities.#_resolveFilter(ast, fragment, context);
    return { filter, diagnostics: context.diagnostics };
  }

  static #_resolveAst(query, fieldDefinitions) {
    if (typeof query === 'string') {
      let implicitOperator = 'and';
      if (fieldDefinitions.defaultOperator === 'or') {
        implicitOperator = 'or';
      }
      return Parser.parse(query, { implicitOperator });
    }
    const isAst = typeof query === 'object' && query !== null
      && query.type === 'query' && Array.isArray(query.diagnostics);
    if (!isAst) {
      throw new TypeError('QUERY_MUST_BE_STRING_OR_AST');
    }
    return query;
  }

  static #_resolveFilter(ast, fragment, context) {
    if (ast.child === null) {
      return {};
    }
    if (context.interactive) {
      if (fragment === null) {
        return {};
      }
      return fragment;
    }
    const hasErrors = context.diagnostics.some((diagnostic) => diagnostic.severity === 'error');
    if (hasErrors) {
      return null;
    }
    return fragment;
  }

  static #_convertNode(node, context) {
    if (node.type === 'clause') {
      return FqlToMqlUtilities.#_convertClause(node, context);
    }
    if (node.type === 'and' || node.type === 'or') {
      return FqlToMqlUtilities.#_convertBoolean(node, context);
    }
    if (node.type === 'not') {
      return FqlToMqlUtilities.#_convertNot(node, context);
    }
    throw new Error('INVALID_AST');
  }

  static #_convertClause(node, context) {
    if (node.field === null) {
      context.diagnostics.push(Diagnostics.error(
        Diagnostics.CODES.BARE_TERM,
        `'${FqlToMqlUtilities.#_describe(node)}' has no field — bare terms are not executable in v1`,
        node.span
      ));
      return null;
    }
    if (node.value.type === 'missing') {
      return null;
    }
    const declaration = context.fields[node.field];
    if (declaration === undefined) {
      context.diagnostics.push(Diagnostics.error(
        Diagnostics.CODES.UNKNOWN_FIELD, `'${node.field}' is not a declared field`, node.span
      ));
      return null;
    }
    if (node.value.type === 'range') {
      return FqlToMqlUtilities.#_convertRange(node, declaration, context);
    }
    if (node.value.type === 'word' && node.value.wildcard === true) {
      if (declaration.type !== 'string') {
        context.diagnostics.push(Diagnostics.error(
          Diagnostics.CODES.WILDCARD_NOT_ALLOWED,
          `'${node.field}' is type '${declaration.type}' — wildcards apply to string fields only`,
          node.span
        ));
        return null;
      }
      return { [node.field]: { $regex: FqlToMqlUtilities.#_wildcardRegex(node.value.text) } };
    }
    if (declaration.type === 'string' && declaration.match === 'contains') {
      return { [node.field]: { $regex: FqlToMqlUtilities.#_escapeRegex(node.value.text) } };
    }
    const coerced = FqlToMqlUtilities.#_coerce(node.value.text, declaration, node, context);
    if (!coerced.ok) {
      return null;
    }
    return { [node.field]: coerced.value };
  }

  static #_convertRange(node, declaration, context) {
    const constraints = {};
    const lower = node.value.lower;
    if (lower !== null) {
      const coerced = FqlToMqlUtilities.#_coerce(lower.text, declaration, node, context);
      if (!coerced.ok) {
        return null;
      }
      if (node.value.includeLower) {
        constraints.$gte = coerced.value;
      } else {
        constraints.$gt = coerced.value;
      }
    }
    const upper = node.value.upper;
    if (upper !== null) {
      const coerced = FqlToMqlUtilities.#_coerce(upper.text, declaration, node, context);
      if (!coerced.ok) {
        return null;
      }
      if (node.value.includeUpper) {
        constraints.$lte = coerced.value;
      } else {
        constraints.$lt = coerced.value;
      }
    }
    if (Object.keys(constraints).length === 0) {
      return {};
    }
    return { [node.field]: constraints };
  }

  static #_convertBoolean(node, context) {
    const fragments = [];
    for (const child of node.children) {
      const fragment = FqlToMqlUtilities.#_convertNode(child, context);
      if (fragment === null) {
        if (context.interactive) {
          FqlToMqlUtilities.#_warnSkipped(child, context);
        }
        continue;
      }
      fragments.push(fragment);
    }
    if (fragments.length === 0) {
      return null;
    }
    if (fragments.length === 1) {
      return fragments[0];
    }
    if (node.type === 'or') {
      const collapsed = FqlToMqlUtilities.#_collapseToIn(fragments);
      if (collapsed !== null) {
        return collapsed;
      }
      return { $or: fragments };
    }
    return { $and: fragments };
  }

  static #_convertNot(node, context) {
    const operand = node.child;
    const fragment = FqlToMqlUtilities.#_convertNode(operand, context);
    if (fragment === null) {
      return null;
    }
    if (operand.type === 'clause') {
      const field = Object.keys(fragment)[0];
      const value = fragment[field];
      if (FqlToMqlUtilities.#_isNegatableOperatorObject(value)) {
        return { [field]: { $not: value } };
      }
      return { [field]: { $ne: value } };
    }
    return { $nor: [fragment] };
  }

  static #_isNegatableOperatorObject(value) {
    if (!FqlToMqlUtilities.#_isPlainObject(value) || FqlToMqlUtilities.#_isExtendedJsonScalar(value)) {
      return false;
    }
    const keys = Object.keys(value);
    return keys.some((key) => key === '$regex' || FqlToMqlUtilities.#_RANGE_OPERATOR_KEYS.has(key));
  }

  static #_collapseToIn(fragments) {
    let field = null;
    const values = [];
    for (const fragment of fragments) {
      const keys = Object.keys(fragment);
      if (keys.length !== 1) {
        return null;
      }
      const value = fragment[keys[0]];
      if (!FqlToMqlUtilities.#_isEqualityValue(value)) {
        return null;
      }
      if (field === null) {
        field = keys[0];
      } else if (field !== keys[0]) {
        return null;
      }
      values.push(value);
    }
    return { [field]: { $in: values } };
  }

  static #_isEqualityValue(value) {
    if (value === null || typeof value !== 'object') {
      return true;
    }
    return FqlToMqlUtilities.#_isExtendedJsonScalar(value);
  }

  static #_isExtendedJsonScalar(value) {
    if (!FqlToMqlUtilities.#_isPlainObject(value)) {
      return false;
    }
    const keys = Object.keys(value);
    return keys.length === 1 && FqlToMqlUtilities.#_EJSON_SCALAR_KEYS.has(keys[0]);
  }

  static #_coerce(text, declaration, node, context) {
    if (declaration.type === 'string') {
      return { ok: true, value: text };
    }
    if (declaration.type === 'number') {
      const value = Number(text);
      if (text.trim() !== '' && Number.isFinite(value)) {
        return { ok: true, value };
      }
      return FqlToMqlUtilities.#_coercionFailure(text, 'number', node, context);
    }
    if (declaration.type === 'boolean') {
      if (text === 'true') {
        return { ok: true, value: true };
      }
      if (text === 'false') {
        return { ok: true, value: false };
      }
      return FqlToMqlUtilities.#_coercionFailure(text, 'boolean', node, context);
    }
    if (declaration.type === 'date') {
      if (FqlToMqlUtilities.#_DATE_RE.test(text) && !Number.isNaN(Date.parse(text))) {
        return { ok: true, value: { $date: new Date(text).toISOString() } };
      }
      return FqlToMqlUtilities.#_coercionFailure(text, 'date', node, context);
    }
    if (declaration.type === 'objectId') {
      if (FqlToMqlUtilities.#_OBJECT_ID_RE.test(text)) {
        return { ok: true, value: { $oid: text } };
      }
      return FqlToMqlUtilities.#_coercionFailure(text, 'objectId', node, context);
    }
    const hit = declaration.values.find((value) => String(value) === text);
    if (hit === undefined) {
      context.diagnostics.push(Diagnostics.error(
        Diagnostics.CODES.VALUE_NOT_IN_ENUM,
        `'${text}' is not one of '${node.field}''s declared values`,
        node.span
      ));
      return { ok: false };
    }
    return { ok: true, value: hit };
  }

  static #_coercionFailure(text, type, node, context) {
    context.diagnostics.push(Diagnostics.error(
      Diagnostics.CODES.COERCION_FAILED,
      `'${text}' does not coerce to ${type} for '${node.field}'`,
      node.span
    ));
    return { ok: false };
  }

  static #_warnSkipped(node, context) {
    context.diagnostics.push(Diagnostics.warning(
      Diagnostics.CODES.CLAUSE_SKIPPED,
      `skipped '${FqlToMqlUtilities.#_describe(node)}'`,
      node.span
    ));
  }

  static #_describe(node) {
    try {
      return Serializer.serialize(node);
    } catch {
      return node.type;
    }
  }

  static #_wildcardRegex(text) {
    let pattern = '^';
    for (const char of text) {
      if (char === '*') {
        pattern += '.*';
      } else if (char === '?') {
        pattern += '.';
      } else {
        pattern += char.replace(FqlToMqlUtilities.#_REGEX_SPECIALS_RE, '\\$&');
      }
    }
    pattern += '$';
    if (pattern.endsWith('.*$')) {
      pattern = pattern.slice(0, -3);
    }
    if (pattern.startsWith('^.*')) {
      pattern = pattern.slice(3);
    }
    return pattern;
  }

  static #_escapeRegex(text) {
    return text.replace(FqlToMqlUtilities.#_REGEX_SPECIALS_RE, '\\$&');
  }

  static #_isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }
}

export { FqlToMqlUtilities };
export default FqlToMqlUtilities;
